export type Limit = 3000000 | 6000000 | 16000000;
export type Deductible = 0 | 150000 | 190000 | 350000 | 500000;
export type Copay = 0 | 0.15 | 0.25;
export type LifeUtilityAddOn = "breeding" | "hunting" | "";

// Sanity cap on signup age, all markets. Since ratings v20260804 the UK
// engine extrapolates past its age table instead of throwing, so GBR no
// longer needs a stricter pricing-driven cap than everyone else.
export const MAX_AGE_DOG = 300;
export const MAX_AGE_CAT = 300;
export const MIN_LIFE_LIMIT_CAT = 1500;
export const MIN_LIFE_LIMIT_DOG = 2500;
export const MAX_LIFE_LIMIT_CAT = 20000;
export const MAX_LIFE_LIMIT_DOG = 50000;

// GBR life cover pays the purchase price, capped per species (backend
// animalPriceCapPence: dog £6,000, cat £3,000). Used to interpolate the cap
// into the GBR Life add-on description.
export const GBR_MAX_LIFE_LIMIT_DOG = 6000;
export const GBR_MAX_LIFE_LIMIT_CAT = 3000;

// GBR life cover minimum purchase price (£) per species — below this the
// add-on can't be bought. Mirrors Sweden's MIN_LIFE_LIMIT_* rule, but in
// pounds (the SWE constants are SEK) so it lives on its own GBR constants.
export const GBR_MIN_LIFE_LIMIT_DOG = 150;
export const GBR_MIN_LIFE_LIMIT_CAT = 50;

// GBR signup purchase-price cap. Life cover pays out the purchase price, so we
// cap the entered value at £10,000 to keep quotes within a sane range.
export const GBR_MAX_PURCHASE_PRICE = 10000;

export const ipids = [
  {
    limit: 3000000,
    href: "https://docs.lassie.co/swe/latest/lassie-ipid-brons.pdf",
  },
  {
    limit: 6000000,
    href: "https://docs.lassie.co/swe/latest/lassie-ipid-silver.pdf",
  },
  {
    limit: 9000000,
    href: "https://docs.lassie.co/swe/latest/lassie-ipid-silver.pdf",
  },
  {
    limit: 12000000,
    href: "https://docs.lassie.co/swe/latest/lassie-ipid-silver.pdf",
  },
  {
    limit: 16000000,
    href: "https://docs.lassie.co/swe/latest/lassie-ipid-guld.pdf",
  },
];

// GBR insurance documents. IPID is plan-tiered (Bronze/Silver/Gold, which map
// to the small/medium/large plan names); policy wording (the UK T&C) is shared
// across tiers. `latest` resolves to the current published version on the docs
// CDN — use it for web where we always want the newest document.
export const gbrIpidByPlan: Record<"small" | "medium" | "large", string> = {
  small: "https://docs.lassie.co/gbr/latest/lassie-ipid-bronze.pdf",
  medium: "https://docs.lassie.co/gbr/latest/lassie-ipid-silver.pdf",
  large: "https://docs.lassie.co/gbr/latest/lassie-ipid-gold.pdf",
};
export const gbrPolicyWording =
  "https://docs.lassie.co/gbr/latest/policy-wording.pdf";

// Resolve the IPID for a plan name to its tier document. The package config has
// a `mini` tier alongside `small`; both are the Bronze product, so map `mini`
// onto `small`. Falls back to Silver (the default plan) for unknown names.
export const gbrIpidForPlan = (plan?: string): string =>
  gbrIpidByPlan[
    (plan === "mini" ? "small" : plan) as keyof typeof gbrIpidByPlan
  ] ?? gbrIpidByPlan.medium;

export const prepurchaseInfoInsurance =
  "https://docs.lassie.co/swe/latest/forkopsinformation-hund-och-kattforsakring.pdf";
export const prepurchaseInfoLassie =
  "https://docs.lassie.co/swe/latest/forkopsinformation-om-lassie.pdf";
export const termsAndConditions =
  "https://docs.lassie.co/swe/latest/villkor-hund-och-kattforsakring.pdf";
export const termsAndConditionsOldV5 =
  "https://docs.lassie.co/terms-and-conditions/villkor-hund-och-kattforsakring-juli-2023.pdf";
export const termsAndConditionsOldV4 =
  "https://docs.lassie.co/terms-and-conditions/villkor-hund-och-kattforsakring-juni-2023.pdf";
export const termsAndConditionsOldV3 =
  "https://docs.lassie.co/terms-and-conditions/villkor-hund-och-kattforsakring-januari-2023.pdf";
export const termsAndConditionsOldV2 =
  "https://docs.lassie.co/terms-and-conditions/villkor-hund-och-kattforsakring-maj-2022-v2.pdf";
export const termsAndConditionsOld =
  "https://docs.lassie.co/terms-and-conditions/villkor-hund-och-kattforsakring-januari-2022.pdf";
export const termsAndConditionsOldV1_2 =
  "https://docs.lassie.co/terms-and-conditions/villkor-hund-och-kattforsakring-maj-2022-v1.2.pdf";
